import { memo, useState, useEffect } from 'react'
import { HeartIcon, MapPinIcon, ChatBubbleLeftRightIcon, BellIcon, BellSlashIcon, CalendarDaysIcon } from '@heroicons/react/24/outline'
import { HeartIcon as HeartIconSolid, BellIcon as BellIconSolid } from '@heroicons/react/24/solid'
import ShareButton from './ShareButton'

/** Small inline spinner */
const Spinner = () => (
  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
)

/**
 * EntityActionButtons - Confirm location + Share/Favorite/Chat action grid
 * Shown in both semi and expanded panel modes.
 * @atomic @idempotent
 */
const EntityActionButtons = memo(function EntityActionButtons({
  handleConfirmLocationClick,
  isOwnSighting,
  handleFavoriteClick,
  isFavorite,
  isTogglingFavorite,
  handleJoinDiscussion,
  isJoiningChannel,
  entityDetails,
  notifyOnSpot = false,
  onToggleNotify = null,
  onBook = null,
  t,
}) {
  const favorited = isFavorite && isFavorite(entityDetails.id)
  const [localNotify, setLocalNotify] = useState(notifyOnSpot)
  const [isTogglingNotify, setIsTogglingNotify] = useState(false)

  // Sync if parent value changes (e.g. entity switched)
  useEffect(() => { setLocalNotify(notifyOnSpot) }, [notifyOnSpot])

  const handleNotifyClick = async (e) => {
    e.stopPropagation()
    if (!onToggleNotify || isTogglingNotify) return
    setIsTogglingNotify(true)
    setLocalNotify(prev => !prev) // optimistic
    try {
      await onToggleNotify()
    } catch {
      setLocalNotify(prev => !prev) // revert on error
    } finally {
      setIsTogglingNotify(false)
    }
  }

  return (
    <>
      <button
        onClick={handleConfirmLocationClick}
        onPointerDown={(e) => { e.preventDefault(); handleConfirmLocationClick(e) }}
        disabled={isOwnSighting}
        style={{ touchAction: 'manipulation' }}
        className={`w-full px-4 py-2.5 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary shadow-sm hover:shadow-md transform hover:scale-[1.01] ${isOwnSighting ? 'bg-gray-300 cursor-not-allowed' : 'bg-primary hover:bg-primary-600'}`}
        title={isOwnSighting ? t('toast.info.cannotConfirmOwn', "You can't confirm your own sighting") : t('map.confirmLocation', 'Confirm Location')}
      >
        <MapPinIcon className="w-5 h-5" />
        <span>{t('map.confirmLocation', 'Confirm Location')}</span>
      </button>

      {onBook && (
        <button
          onClick={(e) => { e.stopPropagation(); onBook(e) }}
          onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); onBook(e) }}
          style={{ touchAction: 'manipulation' }}
          className="w-full px-4 py-2.5 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 text-sm text-white bg-accent hover:opacity-90 shadow-sm hover:shadow-md"
        >
          <CalendarDaysIcon className="w-5 h-5" />
          <span>{t('bookings.request', 'Book a Service')}</span>
        </button>
      )}

      <div className={`grid gap-2 ${favorited ? 'grid-cols-4' : 'grid-cols-3'}`}>
        <ShareButton entityDetails={entityDetails} showLabel label={t('entities.detail.shareLabel', 'Share')} />

        <button
          onClick={handleFavoriteClick}
          onTouchEnd={handleFavoriteClick}
          disabled={isTogglingFavorite}
          title={favorited ? t('favorites.removeFromFavorites', 'Unfavorite') : t('favorites.addToFavorites', 'Favorite')}
          aria-label={favorited ? t('favorites.removeFromFavorites', 'Unfavorite') : t('favorites.addToFavorites', 'Favorite')}
          className={`w-full px-2 py-2.5 rounded-lg font-medium transition-all duration-150 flex flex-col items-center justify-center gap-1.5 border shadow-sm hover:shadow-md ${
            favorited
              ? 'bg-rose-50 text-rose-500 border-rose-200 hover:bg-rose-100'
              : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
          } ${isTogglingFavorite ? 'opacity-50 cursor-wait' : ''}`}
        >
          {isTogglingFavorite ? <Spinner /> : favorited ? <HeartIconSolid className="w-4 h-4 flex-shrink-0" /> : <HeartIcon className="w-4 h-4 flex-shrink-0" />}
          <span className="text-[10px] font-semibold leading-none tracking-wide">
            {favorited ? t('favorites.saved', 'Saved') : t('favorites.save', 'Save')}
          </span>
        </button>

        <button
          onClick={handleJoinDiscussion}
          onTouchEnd={handleJoinDiscussion}
          disabled={isJoiningChannel}
          title={isJoiningChannel ? t('channel.joiningDiscussion', 'Joining...') : t('channel.joinDiscussion', 'Join Discussion')}
          aria-label={isJoiningChannel ? t('channel.joiningDiscussion', 'Joining...') : t('channel.joinDiscussion', 'Join Discussion')}
          className={`w-full px-2 py-2.5 rounded-lg font-medium transition-all duration-150 flex flex-col items-center justify-center gap-1.5 bg-white text-gray-500 border border-gray-200 shadow-sm hover:shadow-md hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 ${isJoiningChannel ? 'opacity-50 cursor-wait' : ''}`}
        >
          {isJoiningChannel ? <Spinner /> : <ChatBubbleLeftRightIcon className="w-4 h-4 flex-shrink-0" />}
          <span className="text-[10px] font-semibold leading-none tracking-wide">{t('channel.chat', 'Chat')}</span>
        </button>

        {favorited && (
          <button
            onClick={handleNotifyClick}
            onTouchEnd={handleNotifyClick}
            disabled={isTogglingNotify || !onToggleNotify}
            title={localNotify ? t('favorites.notifyDisable', 'Stop notifications') : t('favorites.notifyEnable', 'Notify me when spotted')}
            aria-label={localNotify ? t('favorites.notifyDisable', 'Stop notifications') : t('favorites.notifyEnable', 'Notify me when spotted')}
            className={`w-full px-2 py-2.5 rounded-lg font-medium transition-all duration-150 flex flex-col items-center justify-center gap-1.5 border shadow-sm hover:shadow-md ${
              localNotify
                ? 'bg-amber-50 text-amber-500 border-amber-200 hover:bg-amber-100'
                : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
            } ${isTogglingNotify ? 'opacity-50 cursor-wait' : ''}`}
          >
            {isTogglingNotify ? <Spinner /> : localNotify ? <BellIconSolid className="w-4 h-4 flex-shrink-0" /> : <BellIcon className="w-4 h-4 flex-shrink-0" />}
            <span className="text-[10px] font-semibold leading-none tracking-wide">
              {localNotify ? t('favorites.notifyOn', 'Alerts') : t('favorites.notifyOff', 'Alert')}
            </span>
          </button>
        )}
      </div>
    </>
  )
})

export default EntityActionButtons
